import React from 'react'
import { store } from '../../../../../app/store'
import { getUnique } from '../../../../helpers/common'
import { selectPeriod } from '../../../../portfolioInsights/common/period/slice'
import { PortfolioOverviewContent } from '../../../../portfolioInsights/summary/portfolioOverview/Content'
import { CRITERIA } from '../../../../portfolioInsights/summary/portfolioOverview/constant'
import {
  resetStorePortfolioOverview,
  selectAnalyse,
  selectVersus,
} from '../../../../portfolioInsights/summary/portfolioOverview/store/slice'
import { getPortfolioOverview } from '../../../../portfolioInsights/summary/portfolioOverview/store/thunk'
import { CreditRisk } from '../../../../portfolioInsights/summary/topHighest/CreditRisk'
import { Performance } from '../../../../portfolioInsights/summary/topHighest/Performance'
import { resetStorePortfolioTopHighest } from '../../../../portfolioInsights/summary/topHighest/store/slice'
import {
  getTopHighestIndustry,
  getTopHighestPortfolio,
} from '../../../../portfolioInsights/summary/topHighest/store/thunk'
import {
  MAIN_SCREEN,
  SCREEN,
  TYPE_INFORMATION_PANEL,
  TYPE_ITEM_SETTING,
} from '../../constant'

const MODEL_ID = {
  CREDIT_RISK: 1,
  PERFORMANCE: 2,
  CORPORATE_RISK: 3,
}

const PANELS = {
  PORTFOLIO_OVERVIEW: 'portfolioOverview',
  TOP_10_HIGHEST_RISK: 'top10HighestRisk',
  TOP_10_HIGHEST_GROWTH_POTENTIAL: 'top10HighestGrowthPotential',
}

export const getPanelListSummary = () => {
  return [
    {
      screen: SCREEN[MAIN_SCREEN.PORTFOLIO_INSIGHT].SUMMARY,
      panel: PANELS.PORTFOLIO_OVERVIEW,
      label: 'dynamicReport.PORTFOLIO_OVERVIEW',
      type: TYPE_ITEM_SETTING.PANEL,
      setting: {
        period: 'Yearly',
        analyseType: CRITERIA.CREDIT_RISK_SCORE.value,
        versusType: CRITERIA.PERFORMANCE_SCORE.value,
      },
    },
    {
      screen: SCREEN[MAIN_SCREEN.PORTFOLIO_INSIGHT].SUMMARY,
      panel: PANELS.TOP_10_HIGHEST_RISK,
      label: {
        text: 'dynamicReport.TOP_10_HIGHEST_RISK',
        subText: 'dynamicReport.TOP_10_HIGHEST_RISK_SUB',
      },
      type: TYPE_ITEM_SETTING.PANEL,
      setting: {
        period: 'Yearly',
        type: ['portfolios', 'industries'],
      },
    },
    {
      screen: SCREEN[MAIN_SCREEN.PORTFOLIO_INSIGHT].SUMMARY,
      panel: PANELS.TOP_10_HIGHEST_GROWTH_POTENTIAL,
      label: {
        text: 'dynamicReport.TOP_10_HIGHEST_GROWTH_POTENTIAL',
        subText: 'dynamicReport.TOP_10_HIGHEST_GROWTH_POTENTIAL_SUB',
      },

      type: TYPE_ITEM_SETTING.PANEL,
      setting: {
        period: 'Yearly',
        type: ['portfolios', 'industries'],
      },
    },
  ]
}

export const getPanelSummary = (panel, setting = {}) => {
  switch (panel) {
    case PANELS.PORTFOLIO_OVERVIEW: {
      return {
        renderComponent: () => {
          return <PortfolioOverviewContent isDynamicReport />
        },
        thunk: [
          ({ portfolioParams }) =>
            getPortfolioOverview({
              ...portfolioParams,
              timeFrequency: setting.period,
              analyseType: setting.analyseType,
              versusType: setting.versusType,
            }),
        ],
        return: (dispatch) => {
          dispatch(resetStorePortfolioOverview())
        },
        isWaitForMount: false,
        isTable: false,
        width: 1000,
        settingPanel: {
          items: [
            {
              type: TYPE_INFORMATION_PANEL.SELECT,
              key: 'analyseType',
              options: Object.values(CRITERIA),
              title: 'portfolio.summary.ANALYSE',
              onChange: (value, { setting, setValue }) => {
                if (value === setting.versusType) {
                  setValue(
                    'setting.versusType',
                    setting.versusType === CRITERIA.CREDIT_RISK_SCORE.value
                      ? CRITERIA.PERFORMANCE_SCORE.value
                      : CRITERIA.CREDIT_RISK_SCORE.value,
                  )
                }
              },
            },
            {
              type: TYPE_INFORMATION_PANEL.SELECT,
              key: 'versusType',
              options: Object.values(CRITERIA),
              title: 'portfolio.summary.VERSUS',
              onChange: (value, { setting, setValue }) => {
                if (value === setting.analyseType) {
                  setValue(
                    'setting.analyseType',
                    setting.analyseType === CRITERIA.CREDIT_RISK_SCORE.value
                      ? CRITERIA.PERFORMANCE_SCORE.value
                      : CRITERIA.CREDIT_RISK_SCORE.value,
                  )
                }
              },
            },
          ],
        },
      }
    }
    case PANELS.TOP_10_HIGHEST_RISK: {
      const params = {
        timeFrequency: setting.period,
        modelId: MODEL_ID.CORPORATE_RISK,
      }
      const thunkByType = {
        portfolios: ({ portfolioParams }) =>
          getTopHighestPortfolio({ ...portfolioParams, ...params }),
        industries: ({ portfolioParams }) =>
          getTopHighestIndustry({ ...portfolioParams, ...params }),
      }

      return {
        components: [
          setting.type?.includes('portfolios')
            ? () => <CreditRisk isDynamicReport hasTableIndustry={false} />
            : null,
          setting.type?.includes('industries')
            ? () => <CreditRisk isDynamicReport hasTablePortfolio={false} />
            : null,
        ].filter(Boolean),
        thunk: (setting.type || []).map((type) => thunkByType[type]),
        return: (dispatch) => {
          dispatch(resetStorePortfolioTopHighest())
        },
        isWaitForMount: false,
        isTable: true,
        settingPanel: {
          isInline: true,
          items: [
            {
              type: TYPE_INFORMATION_PANEL.RADIO,
              key: 'period',
              value: 'Yearly',
              title: 'portfolio.common.YEAR',
            },
            {
              type: TYPE_INFORMATION_PANEL.RADIO,
              key: 'period',
              value: 'Quarterly',
              title: 'portfolio.common.QUARTER',
            },
            {
              type: TYPE_INFORMATION_PANEL.RADIO,
              key: 'period',
              value: 'Monthly',
              title: 'portfolio.common.MONTH',
            },
            {
              type: TYPE_INFORMATION_PANEL.CHECKBOX,
              key: 'type',
              value: 'portfolios',
              title: 'portfolio.summary.HIGHEST_RISK_PORTFOLIOS',
              style: { width: '100%' },
            },
            {
              type: TYPE_INFORMATION_PANEL.CHECKBOX,
              key: 'type',
              value: 'industries',
              title: 'portfolio.summary.HIGHEST_RISK_INDUSTRIES',
            },
          ],
        },
      }
    }
    case PANELS.TOP_10_HIGHEST_GROWTH_POTENTIAL: {
      const params = {
        timeFrequency: setting.period,
        modelId: MODEL_ID.PERFORMANCE,
      }
      const thunkByType = {
        portfolios: ({ portfolioParams }) =>
          getTopHighestPortfolio({ ...portfolioParams, ...params }),
        industries: ({ portfolioParams }) =>
          getTopHighestIndustry({ ...portfolioParams, ...params }),
      }
      return {
        components: [
          setting.type?.includes('portfolios')
            ? () => <Performance isDynamicReport hasTableIndustry={false} />
            : null,
          setting.type?.includes('industries')
            ? () => <Performance isDynamicReport hasTablePortfolio={false} />
            : null,
        ].filter(Boolean),
        thunk: (setting.type || []).map((type) => thunkByType[type]),
        return: (dispatch) => {
          dispatch(resetStorePortfolioTopHighest())
        },
        isWaitForMount: false,
        isTable: true,
        settingPanel: {
          isInline: true,
          items: [
            {
              type: TYPE_INFORMATION_PANEL.RADIO,
              key: 'period',
              value: 'Yearly',
              title: 'portfolio.common.YEAR',
            },
            {
              type: TYPE_INFORMATION_PANEL.RADIO,
              key: 'period',
              value: 'Quarterly',
              title: 'portfolio.common.QUARTER',
            },
            {
              type: TYPE_INFORMATION_PANEL.RADIO,
              key: 'period',
              value: 'Monthly',
              title: 'portfolio.common.MONTH',
            },
            {
              type: TYPE_INFORMATION_PANEL.CHECKBOX,
              key: 'type',
              value: 'portfolios',
              title: 'portfolio.summary.HIGHEST_GROWTH_POTENTIAL_PORTFOLIOS',
              style: { width: '100%' },
            },
            {
              type: TYPE_INFORMATION_PANEL.CHECKBOX,
              key: 'type',
              value: 'industries',
              title: 'portfolio.summary.HIGHEST_GROWTH_POTENTIAL_INDUSTRIES',
            },
          ],
        },
      }
    }
    default:
      break
  }
}

export const getDefaultSummary = () => {
  const analyse = selectAnalyse(store.getState())
  const versus = selectVersus(store.getState())
  const period = selectPeriod(store.getState())

  const panels = getPanelListSummary().map((panel) => ({
    ...panel,
    id: getUnique(),
    setting: {
      ...panel.setting,
      period,
      analyseType: analyse,
      versusType: versus,
    },
  }))

  return panels
}
